import React, { useEffect } from "react";

import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FormInputFieldCurrency } from "@components-shared/desktop/inputs";
import { ErrorMessage, RequiredMark } from "@components/inputs/shared";
import {
	GamingLimitDataType,
	RegistrationLimitOption,
} from "@data-types/membership/registration";
import classNames from "classnames";

export default function OnboardingRegistrationMonthlyLimit(props: {
	gamingLimitsData: GamingLimitDataType | null;
	labelClassName?: string;
}) {
	const { gamingLimitsData, labelClassName } = props;

	const { t } = useTranslation("registration");
	const { setValue, unregister } = useFormContext();

	const limitOption = useWatch({ name: "limitOption" });
	const isTakeover = limitOption === RegistrationLimitOption.Takeover;

	useEffect(() => {
		if (!limitOption) {
			setValue("limitOption", RegistrationLimitOption.Set);
		}
	}, [limitOption]);

	useEffect(() => {
		if (isTakeover) {
			unregister("limitAmount");
		}
	}, [isTakeover]);

	return (
		<div className="form__group u-mar--bottom--reset">
			<div className={classNames("label", labelClassName)}>
				{t("LIMIT_LABEL")}
				<RequiredMark required />
			</div>

			<ErrorMessage name="limitOption" />

			<LimitOptionRadio
				id="limitOptionSet"
				label="registration:LUGAS_LIMIT_OPTION_SET"
				checkboxClassName="u-mar--bottom--xtny"
				value={RegistrationLimitOption.Set}
			/>
			<LimitOptionRadio
				id="limitOptionTakeover"
				label="registration:LUGAS_LIMIT_OPTION_TAKEOVER"
				value={RegistrationLimitOption.Takeover}
			/>

			{isTakeover ? (
				<div className="u-type--sml u-color--text--secondary u-mar--top--xtny">
					{t("LUGAS_TAKEOVER_DESCRIPTION")}
				</div>
			) : (
				<FormInputFieldCurrency
					name="limitAmount"
					className="form__field u-mar--bottom--reset u-mar--top--xtny"
					inputClassName="input input--sml"
					label="registration:LIMIT_AMOUNT_LABEL"
					labelClassName={labelClassName}
					placeholder="MEMBERSHIP.ACCOUNT_ACTIVATION.MONTHLY_DEPOSIT_LIMIT_PLACEHOLDER"
					pattern={{
						value: /^\d+(\.\d{1,2})?$/,
						message: t(
							"CASINO.BALANCE_TRANSFER.AMOUNT_VALIDATION_ERROR"
						),
					}}
					validate={{
						isEqual: (value: number) => {
							if (!gamingLimitsData) {
								return;
							}

							if (
								value >= gamingLimitsData.minLimit &&
								value <= gamingLimitsData.maxLimit
							) {
								return true;
							}

							return t("LIMIT_RANGE_ERROR", {
								0: gamingLimitsData.minLimit,
								1: gamingLimitsData.maxLimit,
							});
						},
					}}
					required
				/>
			)}
		</div>
	);
}

function LimitOptionRadio(props: {
	id: string;
	label: string;
	checkboxClassName?: string;
	value: RegistrationLimitOption;
}) {
	const { id, label, value, checkboxClassName } = props;

	const { t } = useTranslation();
	const { register } = useFormContext();

	const field = register("limitOption", {
		required: {
			value: true,
			message: "COMMON.REQUIRED_FIELD",
		},
	});

	return (
		<div
			className={classNames(
				"input--radio input--radio--sml",
				checkboxClassName
			)}
		>
			<input
				id={id}
				className="input--radio__box input--radio--sml__box"
				type="radio"
				value={value}
				{...field}
			/>
			<label
				htmlFor={id}
				className="input--radio__label input--radio--sml__label"
			>
				{t(label)}
			</label>
		</div>
	);
}
